import React, { useEffect, useState } from 'react';
import { Col, Form, FormControl, Row } from 'react-bootstrap';
import { isStaff } from '../../../../helpers/global';
import AssignedProcessorsMultiSelect from '../../../AssignedProcessors';
import CustomCreatableSelect from '../../../common/CreatableSelect';
import { useGetUnderwriterCertifications } from '../../hooks/useGetUnderwriterCertifications';

const initialFormFieldError = [
  { key: 'status', isNotValid: false, type: 'string' },
  { key: 'assignedProcessors', isNotValid: false, type: 'array' },
  { key: 'hardCopy', isNotValid: false, type: 'string' },
  { key: 'titleCertification', isNotValid: false, type: 'string' },
  { key: 'buyerCertification', isNotValid: false, type: 'string' },
  { key: 'lenderCertification', isNotValid: false, type: 'string' },
  { key: 'underwriterCertification', isNotValid: false, type: 'object' }
];

// function to update the field on change
const updateOnChangeErrorField = (fieldError = [], key) => {
  return fieldError.map(field => {
    if (field.key === key) {
      return { key, isNotValid: false, type: field.type };
    } else {
      return field;
    }
  });
};

export const Survey = ({ defaultValues = {}, role, surveyor, formData, setFormData, errors }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [underwriterCertificationOptions, setUnderwriterCertificationOptions] = useState([]);
  const [formFieldError, setFormFieldError] = useState(
    isStaff(role)
      ? initialFormFieldError
      : initialFormFieldError.filter(v => v.key !== 'status' && v.key !== 'assignedProcessors')
  );
  const underwriterCertificationList = useGetUnderwriterCertifications();
  const surveyorOptions = surveyor.map(s => ({ label: s, value: s }));

  useEffect(() => {
    if (underwriterCertificationList.length > 0) {
      const options = [...underwriterCertificationList].map(u => ({
        label: u,
        value: u
      }));
      setUnderwriterCertificationOptions(options);
      setIsLoading(false);
      setFormData(prev => ({
        ...prev,
        ...defaultValues,
        underwriterCertification: formData.underwriterCertification || underwriterCertificationList[0]
      }));
    }
  }, [underwriterCertificationList]);

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...defaultValues,
        hardCopy: defaultValues.hardCopy ? 'yes' : 'no',
        underwriterCertification: defaultValues.underwriterCertification
      }));
    }
  }, [defaultValues]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'radio' ? (checked ? value : prev[name]) : value
    }));
  };

  const onFormDataChange = (field, e) => {
    if (e && e?.target) {
      const updatedFormData = { ...formData };
      updatedFormData[field] = e.target.value;
      setFormData(updatedFormData);
      setFormFieldError(updateOnChangeErrorField(formFieldError, field));
    }
  };

  const handleAssignedProcessorsChange = e => {
    const assignedProcessorIds = e.map(a => a._id);
    setFormData(prevData => ({ ...prevData, assignedProcessors: assignedProcessorIds }));
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <p className="col-title my-2">Survey Details</p>
      <Row className="align-items-end">
        {isStaff(role) && (
          <Col md={3} xs={12}>
            <Form.Group className="midFont">
              <Form.Label>
                Assigned Processors<span className="text-danger">*</span>
              </Form.Label>
              <AssignedProcessorsMultiSelect
                id="surveyAssignedProcessors"
                error={errors.surveyAssignedProcessors}
                onChange={handleAssignedProcessorsChange}
                selectedProcessorsID={formData.assignedProcessors}
              />
              {errors.surveyAssignedProcessors && (
                <p className="xsmallFont text-rush">{errors.surveyAssignedProcessors}</p>
              )}
            </Form.Group>
          </Col>
        )}

        <Col md={3} xs={12}>
          <Form.Group className="midFont">
            <Form.Label>Listing Agent Name</Form.Label>
            <input
              type="text"
              name="listingAgentName"
              placeholder="Enter name"
              value={formData.listingAgentName}
              onChange={handleChange}
              className={`form-control form-control-sm ${errors.listingAgentName ? 'is-invalid' : ''}`}
            />
            {errors.listingAgentName && <p className="xsmallFont text-rush">{errors.listingAgentName}</p>}
          </Form.Group>
        </Col>
        <Col md={3} xs={12}>
          <Form.Group className="midFont">
            <Form.Label>Listing Agent Phone</Form.Label>
            <input
              required
              type="tel"
              name="listingAgentPhone"
              placeholder="Enter phone number"
              value={formData.listingAgentPhone}
              onChange={handleChange}
              className={`form-control form-control-sm ${errors.listingAgentPhone ? 'is-invalid' : ''}`}
            />
            {errors.listingAgentPhone && <p className="xsmallFont text-rush">{errors.listingAgentPhone}</p>}
          </Form.Group>
        </Col>
        <Col md={3} xs={12}>
          <Form.Group className="midFont">
            <Form.Label>
              Title Certification<span className="text-danger">*</span>
            </Form.Label>
            <input
              required
              type="text"
              name="titleCertification"
              placeholder="Enter title certification"
              value={formData.titleCertification}
              onChange={handleChange}
              className={`form-control form-control-sm ${errors.titleCertification ? 'is-invalid' : ''}`}
            />
            {errors.titleCertification && <p className="xsmallFont text-rush">{errors.titleCertification}</p>}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={3} xs={12}>
          <Form.Group className="midFont">
            <Form.Label>
              Lender Certification<span className="text-danger">*</span>
            </Form.Label>
            <input
              required
              type="text"
              name="lenderCertification"
              placeholder="Enter lender certification"
              value={formData.lenderCertification}
              onChange={handleChange}
              className={`form-control form-control-sm ${errors.lenderCertification ? 'is-invalid' : ''}`}
            />
            {errors.lenderCertification && <p className="xsmallFont text-rush">{errors.lenderCertification}</p>}
          </Form.Group>
        </Col>
        <Col md={3} xs={12}>
          <Form.Group className="midFont">
            <Form.Label>
              Buyer Certification<span className="text-danger">*</span>
            </Form.Label>
            <input
              required
              type="text"
              name="buyerCertification"
              placeholder="Enter buyer certification"
              value={formData.buyerCertification}
              onChange={handleChange}
              className={`form-control form-control-sm ${errors.buyerCertification ? 'is-invalid' : ''}`}
            />
            {errors.buyerCertification && <p className="xsmallFont text-rush">{errors.buyerCertification}</p>}
          </Form.Group>
        </Col>
        <Col md={3} xs={12}>
          <Form.Group className="midFont">
            <Form.Label>
              Underwriter Certification<span className="text-danger">*</span>
            </Form.Label>
            <CustomCreatableSelect
              id="underwriterCertification"
              name="underwriterCertification"
              required
              isLoading={isLoading}
              placeholder="Underwriter Certification"
              options={underwriterCertificationOptions}
              value={
                formData.underwriterCertification
                  ? { label: formData.underwriterCertification, value: formData.underwriterCertification }
                  : underwriterCertificationOptions[0]
              }
              className="basic-single select-organization"
              onChange={e =>
                setFormData({
                  ...formData,
                  underwriterCertification: e?.value || ''
                })
              }
              menuPlacement="auto"
            />
            {errors.underwriterCertification && (
              <p className="xsmallFont text-rush">{errors.underwriterCertification}</p>
            )}
          </Form.Group>
        </Col>
        <Col md={3} xs={12}>
          <Form.Group className="midFont">
            <Form.Label>
              Hard Copy<span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex mt-1">
              <div className="d-flex">
                <FormControl
                  required
                  type="radio"
                  name="hardCopy"
                  value="yes"
                  checked={formData.hardCopy === 'yes'}
                  onChange={handleChange}
                />
                <label style={{ fontSize: 14 }} className="ml-2 my-auto">
                  Yes
                </label>
              </div>
              <div className="d-flex ml-2">
                <FormControl
                  type="radio"
                  name="hardCopy"
                  value="no"
                  checked={formData.hardCopy === 'no'}
                  onChange={handleChange}
                />
                <label style={{ fontSize: 14 }} className="ml-2 my-auto">
                  No
                </label>
              </div>
            </div>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};
